/**
 * CONTRACT.md §2.10 — key/value rows.
 *
 * `<dl>` / `<dt>` / `<dd>`, because that is what the markup means and app.css
 * styles both spellings (`.kv dt,.kv .k`). The `.k`/`.v` span form the mockups
 * also use is the same rows in a `<div>`; one spelling here is enough, and the
 * semantic one is the one to keep.
 *
 * §5 refused `.kv--prose`: "`.kv` is a money column; four sentences are not a
 * column." Left-aligned sentence values are a `Stack gap="tight"` of
 * `<Small><B>Label</B> — …</Small>`.
 */

import type { ReactNode } from 'react'

import { cx, type Common } from './common.js'

export type KvProps = Common & {
  /** `.kv--bare` — no row hairlines. Use inside a `Region`, where space is enough. */
  readonly bare?: boolean | undefined
  readonly children: ReactNode
}

export function Kv({ bare, children, ...common }: KvProps) {
  return (
    <dl className={cx('kv', bare && 'kv--bare')} {...common}>
      {children}
    </dl>
  )
}

export type KvRowProps = Common & {
  readonly k: ReactNode
  readonly v: ReactNode
}

/**
 * The `<div>` between the `<dl>` and its pair is app.css's own: `.kv > div` is
 * the flex row, and a `<dt>`/`<dd>` pair with nothing around it could not be.
 */
export function KvRow({ k, v, ...common }: KvRowProps) {
  return (
    <div {...common}>
      <dt>{k}</dt>
      <dd>{v}</dd>
    </div>
  )
}
